import { reactive, ref } from 'vue'
import axios from '../plugins/axios'
import { user } from '@/core/auth'
import { showNotif } from '@/core/utility'

export const listPoli = ref([])
export const listBranchPoli = ref([])
export const listDoctorPoli = ref([])
export const listDoctorSchedulePoli = ref([])
export const listAnamnesaPoli = ref([])

export const selectedPoli = ref(null)
export const selectedBranchPoli = ref(null)
export const selectedDoctorPoli = ref(null)
export const selectedDoctorSchedulePoli = ref(null)
export const selectedDatePoli = ref(null)
export const selectedPatientPoli = ref(user.value) // default datanya sendiri
export const isSelfPatientPoli = ref(1) // default daftar sendiri

export const loading = reactive({
  listPoli: false,
  listBranchPoli: false,
  listDoctorPoli: false,
  listDoctorSchedulePoli: false,
  registerPoli: false,
  listAnamnesaPoli: false,
  submitAnamnesaPoli: false
})

export const getListPoli = async () => {
  try {
    loading.listPoli = true
    listPoli.value = []
    const { data } = await axios.get('poli/list_poli')

    if (data.success) {
      listPoli.value = data.data
    }
  } catch (error) {
    console.error('error get list poli = ', error)
    throw error
  } finally {
    loading.listPoli = false
  }
}

export const getListBranchPoli = async () => {
  try {
    loading.listBranchPoli = true
    listBranchPoli.value = []
    const { data } = await axios.get('poli/list_poli_cabang', {
      params: {
        poli_kode: selectedPoli.value.poli_kode
      }
    })

    if (data.success) {
      listBranchPoli.value = data.data
    }
  } catch (error) {
    console.error('error get list branch poli = ', error)
    throw error
  } finally {
    loading.listBranchPoli = false
  }
}

export const getListDoctorPoli = async () => {
  try {
    loading.listDoctorPoli = true
    listDoctorPoli.value = []

    if (!selectedPoli.value || !selectedBranchPoli.value) {
      return showNotif({
        status: 'error',
        message: 'Harus pilih poli dan cabang terlebih dahulu'
      })
    }

    const { data } = await axios.get('poli/list_poli_dokter', {
      params: {
        id_cabang: selectedBranchPoli.value.id
      }
    })

    if (data.success) {
      listDoctorPoli.value = data.data
    }
  } catch (error) {
    console.error('error get list doctor poli = ', error)
    throw error
  } finally {
    loading.listDoctorPoli = false
  }
}

/**
 * Pre-select helpers from query params (used by Checkout page)
 * These functions intentionally avoid emitting notifications and
 * simply attempt to hydrate selections based on current lists.
 * They should be called before component watchers are registered
 * to avoid triggering downstream resets.
 */
export const preselectPoliFromQuery = async (query) => {
  try {
    if (!query || !query.poli) return

    // Ensure poli list is loaded
    if (!Array.isArray(listPoli.value) || listPoli.value.length === 0) {
      await getListPoli()
    }

    const kodeFromQuery = query.poli

    // find parent poli that contains the detail with the given kode
    const parent = (listPoli.value || []).find((p) => Array.isArray(p.detail) && p.detail.some((d) => d.poli_kode === kodeFromQuery))
    if (parent) {
      const detail = parent.detail.find((d) => d.poli_kode === kodeFromQuery)
      selectedPoli.value = {
        ...parent,
        poli_kode: detail.poli_kode,
        poli_nama: detail.poli_nama
      }
      return
    }

    // fallback: try matching top-level if any
    const topLevel = (listPoli.value || []).find((p) => p.poli_kode === kodeFromQuery)
    if (topLevel) {
      selectedPoli.value = topLevel
    }
  } catch (e) {
    // swallow errors to keep flow
  }
}

export const preselectBranchFromQuery = async (query) => {
  try {
    if (!query || !query.cabang) return
    if (!selectedPoli.value) return

    // Ensure branch list is loaded for current poli
    if (!Array.isArray(listBranchPoli.value) || listBranchPoli.value.length === 0) {
      await getListBranchPoli()
    }

    const idCabang = String(query.cabang)
    const found = (listBranchPoli.value || []).find((b) => String(b.id) === idCabang)
    if (found) {
      selectedBranchPoli.value = found
    }
  } catch (e) {
    // swallow errors
  }
}

export const preselectDoctorFromQuery = async (query) => {
  try {
    if (!query || !query.dokter) return
    if (!selectedPoli.value || !selectedBranchPoli.value) return

    // Ensure doctor list is loaded for current branch
    if (!Array.isArray(listDoctorPoli.value) || listDoctorPoli.value.length === 0) {
      await getListDoctorPoli()
    }

    const idDokter = String(query.dokter)
    const found = (listDoctorPoli.value || []).find((d) => String(d.id) === idDokter)
    if (found) {
      selectedDoctorPoli.value = found
    }
  } catch (e) {
    // swallow errors
  }
}

export const getListDoctorSchedulePoli = async () => {
  try {
    loading.listDoctorSchedulePoli = true
    listDoctorSchedulePoli.value = []

    if (!selectedPoli.value || !selectedBranchPoli.value || !selectedDoctorPoli.value || !selectedDatePoli.value) {
      return showNotif({
        status: 'error',
        message: 'Harus pilih poli, cabang, dokter dan tanggal terlebih dahulu'
      })
    }

    const { data } = await axios.get('poli/list_jadwal_dokter', {
      params: {
        id: selectedDoctorPoli.value.id, // id dokter poli yang dipilih
        tanggal: selectedDatePoli.value // format 2025-08-28
      }
    })

    if (data.success) {
      listDoctorSchedulePoli.value = data.data
    }
  } catch (error) {
    console.error('error get list doctor schedule poli = ', error)
    throw error
  } finally {
    loading.listDoctorSchedulePoli = false
  }
}

export const postRegisterPoli = async () => {
  try {
    loading.registerPoli = true

    if (!selectedPoli.value || !selectedBranchPoli.value || !selectedDoctorPoli.value || !selectedDoctorSchedulePoli.value) {
      return showNotif({
        status: 'error',
        message: 'Harus pilih poli, cabang, dokter dan jadwal terlebih dahulu'
      })
    }

    const { data } = await axios.post('poli/daftar', {
      device: 'WEB',
      status_periksa: isSelfPatientPoli.value, // 1 = Periksa Sendiri 2 = Periksa Anggota Keluarga
      pas_id_periksa: selectedPatientPoli.value.pas_id,
      cabang_periksa: selectedBranchPoli.value.id,
      id_app_poli_dokter_jadwal: selectedDoctorSchedulePoli.value.id_app_poli_dokter_jadwal,
      poli_kode: selectedPoli.value.poli_kode,
      tanggal: selectedDatePoli.value
    }, {
      headers: {
        id_user: user.value.id,
        token: user.value.token_api
      }
    })

    if (data.success) {
      return data.data
    }
  } catch (error) {
    console.error('error post register poli = ', error)
    throw error
  } finally {
    loading.registerPoli = false
  }
}

export const getAnamnesaPoli = async (anamnesaCode) => {
  try {
    loading.listAnamnesaPoli = true
    listAnamnesaPoli.value = []

    const { data } = await axios.post('poli/anamnesa', {
      anamnesa_code: anamnesaCode
    }, {
      headers: {
        id_user: user.value.id,
        token: user.value.token_api
      }
    })

    if (data.success) {
      listAnamnesaPoli.value = data.data
    }
  } catch (error) {
    console.error('error get anamnesa poli = ', error)
    throw error
  } finally {
    loading.listAnamnesaPoli = false
  }
}

export const submitAnamnesaPoli = async (payload) => {
  try {
    loading.submitAnamnesaPoli = true

    const { data } = await axios.post('poli/anamnesa_simpan', payload, {
      headers: {
        id_user: user.value.id,
        token: user.value.token_api
      }
    })

    if (data.success) {
      return {
        status: true,
        message: 'Anamnesa berhasil disimpan'
      }
    }

    return {
      status: false,
      message: 'Anamnesa gagal disimpan'
    }
  } catch (error) {
    console.error('error submit anamnesa poli = ', error)
    throw error
  } finally {
    loading.submitAnamnesaPoli = false
  }
}
